<template>
  <div>
    <page-header
      has-return
      :has-save="provider.ssn"
      :page="page"
      @onReturn="$router.push({ name: 'ProviderSearch' })"
      @onSave="save"
    />
    <div>
      <validation-observer
        ref="provider-fisical-direction-form"
        novalidate
        slim
      >
        <div v-if="!provider.ssn">
          <div class="bg-secondary-9 p-3">
            <div class="row">
              <div class="col-md-4">
                <div class="ml-2 mb-4">
                  <icon-view
                    icon="fa fa-search pb-1"
                    icon-background="#fff"
                    icon-color="#F57E4A"
                    title="Búsqueda de SSN"
                  />
                </div>
                <div
                  class="col-12 col-sm d-flex flex-sm-column flex-md-row align-items-start"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    class="flex-grow-1"
                    :custom-messages="{
                      required: 'El número de seguro social es requerido.',
                      completePersonalSsn:
                        'El número de seguro social no es válido.',
                    }"
                    rules="completePersonalSsn|required"
                  >
                    <custom-input
                      v-model="ssn"
                      class="mt-n2"
                      :error="errors[0]"
                      label="SSN Patronal:"
                      mask-type="SSNPatronal"
                      placeholder="xxx-xxxxxx"
                      type="text"
                    />
                  </validation-provider>
                  <action-button
                    class="mt-4 px-4 ml-4"
                    i="fa fa-search"
                    text="Buscar"
                    type="submit"
                    variant="success"
                    @click="validateSsn()"
                  />
                </div>
              </div>
            </div>
            <div v-if="searchApplied">
              <custom-separator
                class="my-4"
                text="Resultados de Propuesta de Servicios"
              />
              <div v-if="proposals.length">
                <proposal-request-result-row
                  v-for="(item, index) in proposals"
                  :key="index"
                  class="m-3"
                  :data="item"
                  @select="onSelectProposalRequestHandler"
                />
              </div>
              <custom-result-not-found
                v-else
                text="No hay información disponible"
              />
            </div>
          </div>
        </div>
        <div v-else>
          <div class="my-3 col-12">
            <provider-general-info-form
              ref="generalInfoForm"
              :has-primary-provider="provider.primaryProvider.ssn != ''"
              :proposal-info="proposalInfo"
              :provider-info="provider"
            />
          </div>

          <div class="my-3 col-12">
            <provider-create-fisical-direction-form
              ref="physicalAddressForm"
              :proposal-info="proposalInfo"
            />
          </div>

          <div class="my-3 col-12">
            <provider-create-primary-contact-information
              ref="primaryContactForm"
              :civil-statuses="civilStatuses"
              :proposal-info="proposalInfo"
            />
          </div>
        </div>
      </validation-observer>
    </div>

    <custom-modal
      cancel-text="No"
      icon="fa fa-exclamation"
      save-text="Sí"
      title="Aviso"
      :value="modal.show"
      @cancel="modalCanceled"
      @save="modalConfirmed"
    >
      {{ Message }}
    </custom-modal>
  </div>
</template>

<script>
import ProviderCreateFisicalDirectionForm from './_components/ProviderCreateFisicalDirectionForm';
import ProviderCreatePrimaryContactInformation from './_components/ProviderCreatePrimaryContactInformation';
import { mapGetters } from 'vuex';
import PageHeader from '@/components/common/page/PageHeader';
import ProviderGeneralInfoForm from './_components/ProviderGeneralInfoForm';
import ProposalRequestResultRow from './_components/ProposalRequestResultRow';
import {
  getProviderProposalInfo as _getProviderProposalInfo,
  getProviderProposals as _getProviderProposals,
  getProviderProposalValidation as _getProviderProposalValidation,
  existSSNInRecommendationProposalRequest,
  getCivilStatuses,
} from '@/modules/provider/api';
import CustomModal from '@/components/common/CustomModal';
import { modulesName } from '@/store';
import { mask } from 'ke-the-mask';
import alertMixin from 'utils/mixins/alert.mixin.js';
import CustomInput from 'CustomInput';
import storeMixin from '@/shared/mixins/storeGenerator';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import IconView from 'icon-view';
import companyTypes from '@/utils/constants/companyTypes.js';
import { name } from '@/modules/provider/store';

export default {
  name: 'ProviderCreate',
  components: {
    ProviderGeneralInfoForm,
    ProviderCreateFisicalDirectionForm,
    ProviderCreatePrimaryContactInformation,
    PageHeader,
    CustomModal,
    CustomInput,
    CustomSeparator,
    CustomResultNotFound,
    ProposalRequestResultRow,
    IconView,
  },
  directives: {
    mask,
  },
  inject: ['page'],
  mixins: [alertMixin, storeMixin],
  data() {
    return {
      ssn: null,
      proposals: [],
      searchApplied: false,
      provider: {
        ssn: null,
        primaryProvider: {
          ssn: null,
          name: null,
          providerType: null,
        },
        primaryContactInformation: {
          initial: null,
          name: null,
          department: null,
          phone: null,
          lastName: null,
          position: null,
          workPhone: null,
          motherLastName: null,
          email: null,
          fax: null,
        },
      },
      modal: {
        show: false,
      },
      page: {
        title: 'Proveedor',
        icon: 'orange-icon fas fa-user-tie',
        routes: ['Proveedor', 'Proveedores', 'Añadir Proveedor'],
      },
      proposalInfo: null,
      civilStatuses: [],
    };
  },
  computed: {
    ...mapGetters(modulesName.commonModuleName, ['getValidationMessage']),
    companyTypeId() {
      return this.$route.params.companyTypeId ?? null;
    },
  },
  async created() {
    await this.validationMessagesByCode();
  },
  async mounted() {
    const { data } = await getCivilStatuses();
    this.civilStatuses = [...data];
  },
  methods: {
    async save() {
      const formRefs = [
        'generalInfoForm',
        'physicalAddressForm',
        'primaryContactForm',
      ];
      const promises = await formRefs.map(
        async (ref) => await this.$refs[ref].validate()
      );
      const forms = await Promise.all([...promises]);
      const allValids = forms.every((x) => x.valid);
      const data = forms.reduce(
        (x, { data }, i) => ({ ...x, [formRefs[i]]: data }),
        {}
      );

      if (allValids) {
        let result = await this.$store.dispatch(`${name}/save`, {
          ...data,
          ssn: this.ssn,
          proposalNumber:
            this.proposals.length > 0 ? this.proposals[0].proposalNumber : null,
          isMainCompany: !this.provider.primaryProvider.ssn,
          parentMainCompanyId: this.provider.primaryProvider.id,
        });

        this.$router.push({
          name: 'provider-edit',
          params: {
            companyId: result.id,
            editMode: false,
            fiscalYearId: result.fiscalYearId,
          },
        });

        this.Toast('Proceso exitoso', 'Los datos fueron guardado', 'success');
      }
    },
    async validateSsn() {
      const isValid = await this.$refs[
        'provider-fisical-direction-form'
      ].validate();

      if (!isValid) {
        this.ShowToast('Atención', 'Información Incompleta.', 'error');
        return;
      }
      const { data: existSSN } = await existSSNInRecommendationProposalRequest(
        this.ssn
      );
      if (
        !existSSN &&
        this.companyTypeId != companyTypes.DirectPaymentInstitutions
      ) {
        this.ShowToast(
          'Error',
          `Favor verificar que el proveedor que desea crear se encuentre en estatus Recomendada en la Propuesta de Servicios para el año ${
            this.currentAcademicYear().description
          }.`,
          'error'
        );
        return;
      }
      if (existSSN) {
        this.searchApplied = true;
        await this.getProviderProposals();
      } else {
        this.provider.primaryProvider.providerType = this.companyTypeId;
        this.provider.primaryProvider.ssn = this.ssn;
        this.provider.ssn = this.ssn;
      }
    },
    async getProviderProposals() {
      try {
        const { data } = await _getProviderProposals(this.ssn);
        this.proposals = data;
      } catch {
        this.ShowToast('Error', 'Ocurrió un error cargando los datos', 'error');
      }
    },
    async getProviderProposalInfo(id) {
      try {
        const { data } = await _getProviderProposalInfo(id);
        this.proposalInfo = data;
        this.provider.ssn = data.ssn;
      } catch (ex) {
        this.ShowToast('Error', 'Ocurrió un error cargando los datos', 'error');
      }
    },
    async onSelectProposalRequestHandler({ id }) {
      try {
        const { data } = await _getProviderProposalValidation(id);
        if (data.hasCompany) {
          this.ShowToast(
            'Error',
            'La propuesta de servicio seleccionada ya cuenta con un perfil de proveedor en sistema.',
            'error'
          );
          return;
        }
        if (!data.isRecommended) {
          this.ShowToast(
            'Error',
            'La propuesta de servicio seleccionada no se encuentra Recomendada en sistema.',
            'error'
          );
          return;
        }
        await this.getProviderProposalInfo(id);
      } catch (error) {
        const message =
          error?.response?.data.message ??
          'Ocurrió un error cargando la propuesta de servicio';
        this.ShowToast('Error', message, 'error');
      }
    },
    async confirm() {
      this.modal.show = true;
    },
    goToProviderSearch() {
      this.$router.push({ name: 'provider' });
    },
    modalConfirmed() {
      this.modal.show = false;
    },
    modalCanceled() {
      this.modal.show = false;
      this.goToProviderSearch();
    },
    async validationMessagesByCode() {
      try {
        await this.getValidationMessages(['CMP1002']);
        this.Message = this.getValidationMessage('CMP1002');
      } catch {
        this.Message =
          'Ya existe un proveedor definido con el número de Seguro Social Patronal entrado. ¿Desea crear el proveedor como secundario?';
      }
    },
  },
};
</script>

<style lang="sass">
.mipe-tab
  border-bottom: 1px solid #E2E2E2
  .nav-item
    .nav-link
      font: 16px Lato
      letter-spacing: 0
      padding-bottom: 5px

.mipe-active-tab
  border: 0 !important
  border-bottom: 3px solid var(--mipe-orange) !important
  &::after
    display: none

@media only screen and (max-width: 768px)
  .box-container-custom
    width: 100% !important
</style>
